import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Truck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { BRAND, SERVICE_TYPES } from "../lib/brand";

export default function Pickup() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    pickup_address: "",
    city: "",
    province_state: "",
    country: "Canada",
    destination: "Jamaica",
    service_type: "Sea Freight",
    items_description: "",
    preferred_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/pickup-requests", form);
      setDone(true);
      toast.success("Pickup request submitted!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#F1F5F9]" data-testid="pickup-success">
        <div className="max-w-md text-center bg-white p-10 border border-slate-200 rounded-sm">
          <CheckCircle2 className="h-14 w-14 text-[#1E2A78] mx-auto mb-4" />
          <h2 className="text-2xl font-display font-bold text-[#0F172A]">Pickup scheduled</h2>
          <p className="mt-2 text-slate-600">We'll call or email you to confirm pickup date and time within 24 hours.</p>
          <button onClick={() => { setDone(false); setForm({ ...form, items_description: "", notes: "" }); }} className="mt-6 px-5 py-2 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200]">
            Submit another
          </button>
        </div>
      </div>
    );
  }

  const input = "w-full px-4 py-3 bg-white border border-slate-200 rounded-sm outline-none focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/20 text-sm";
  const label = "block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2";

  return (
    <div data-testid="pickup-page">
      <section className="bg-[#1E2A78] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 text-[#FFD700] mb-3">
            <Truck className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Cargo Pickup</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight max-w-3xl">
            Request a <span className="text-[#FFD700]">free pickup.</span>
          </h1>
          <p className="mt-6 text-slate-200 max-w-2xl">
            Tell us where to come and what you're shipping. Our driver will be there with manifest forms ready.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={submit} className="space-y-5 bg-white border border-slate-200 p-8 rounded-sm" data-testid="pickup-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>Full Name</label>
                <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={input} data-testid="pickup-name" />
              </div>
              <div>
                <label className={label}>Phone</label>
                <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={input} data-testid="pickup-phone" />
              </div>
            </div>
            <div>
              <label className={label}>Email</label>
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={input} data-testid="pickup-email" />
            </div>
            <div>
              <label className={label}>Pickup Address</label>
              <input required value={form.pickup_address} onChange={(e) => set("pickup_address", e.target.value)} className={input} data-testid="pickup-address" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className={label}>City</label>
                <input required value={form.city} onChange={(e) => set("city", e.target.value)} className={input} data-testid="pickup-city" />
              </div>
              <div>
                <label className={label}>Province / State</label>
                <input required value={form.province_state} onChange={(e) => set("province_state", e.target.value)} className={input} data-testid="pickup-region" />
              </div>
              <div>
                <label className={label}>Country</label>
                <select value={form.country} onChange={(e) => set("country", e.target.value)} className={input} data-testid="pickup-country">
                  <option>Canada</option>
                  <option>USA</option>
                </select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>Destination</label>
                <select value={form.destination} onChange={(e) => set("destination", e.target.value)} className={input} data-testid="pickup-destination">
                  {BRAND.destinations.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Service Type</label>
                <select value={form.service_type} onChange={(e) => set("service_type", e.target.value)} className={input} data-testid="pickup-service">
                  {SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={label}>What are you shipping?</label>
              <textarea required rows="3" value={form.items_description} onChange={(e) => set("items_description", e.target.value)} className={input} placeholder="e.g. 3 food barrels, 1 box of clothing, 1 TV..." data-testid="pickup-items" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>Preferred Pickup Date</label>
                <input type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className={input} data-testid="pickup-date" />
              </div>
              <div>
                <label className={label}>Additional Notes</label>
                <input value={form.notes} onChange={(e) => set("notes", e.target.value)} className={input} data-testid="pickup-notes" />
              </div>
            </div>
            <button type="submit" disabled={submitting} className="w-full px-6 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] disabled:opacity-60" data-testid="pickup-submit">
              {submitting ? "Submitting..." : "Request Free Pickup"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
